import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const continuousBatchingComponent: ComponentDef = {
  id: 'continuous-batching',
  name: 'Continuous Batching',
  description: 'Dynamically batch arriving inference requests to keep the GPU saturated.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'batch_size', label: 'Batch size', type: 'number', default: 4, min: 1, max: 128, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'continuous',
      options: [
        { label: 'Continuous Batching', value: 'continuous' },
        { label: 'Iteration-Level Scheduling', value: 'iteration_level' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const batch_size = Number(params.batch_size);
    const variant = String(params.variant);

    const g = new GraphBuilder();

    if (variant === 'iteration_level') {
      // Iteration-level scheduling: prefill and decode share the same batch at each step
      // New requests join mid-batch, completed requests leave
      g.input('new_requests', [batch_size, d_model]);
      g.input('running_requests', [batch_size, d_model]);

      // Concatenate new + running into a unified batch
      g.concat('unified_batch', ['new_requests', 'running_requests'], [batch_size * 2, d_model]);

      // Run batch attention (prefill for new, decode for running)
      g.linear('batch_attn', 'unified_batch', [d_model, d_model]);

      // Split completed vs still-running
      g.split('completed', 'batch_attn', [batch_size, d_model], 'completed');
      g.split('still_running', 'batch_attn', [batch_size, d_model], 'running');

      // Output completed sequences, re-enqueue running ones
      g.output('out', 'completed');
    } else {
      // Continuous Batching (default): pad and batch arriving requests
      const requestIds: string[] = [];
      for (let i = 0; i < batch_size; i++) {
        const id = `req_${i}`;
        g.input(id, [seq_len, d_model]);
        requestIds.push(id);
      }

      g.concat('padded_batch', requestIds, [batch_size, seq_len, d_model]);
      g.router('scheduler', 'padded_batch', [batch_size, seq_len, d_model]);
      g.linear('model', 'scheduler', [d_model, d_model]);
      g.output('out', 'model');
    }

    return g.build();
  }
};
